import Chat from "@/entities/Chat";
import Message from "@/entities/Message";
import { createSignal } from "@rasenganjs/signals";

export type ChatsState = {
	chats: Chat[];
	selectedChatId: string;
	isGenerating: boolean;
	prompt: string;
};

export type ChatsActions = {
	create: (chat: Chat) => void;
	reset: () => void;
	addMessage: ({
		chatId,
		message,
	}: {
		chatId: string;
		message: Message;
	}) => void;
	selectChat: (chat: Chat) => void;
	addResponse: ({
		chatId,
		messageId,
		content,
	}: {
		chatId: string;
		messageId: string;
		content: string;
	}) => void;
	stopGenerating: () => void;
	renameChat: ({ chatId, name }: { chatId: string; name: string }) => void;
  updatePrompt: (prompt: string) => void;
};

export type ChatsOperations = {
	getCurrentChat: (id: string) => Chat | undefined;
};

export default createSignal<ChatsState>({
	name: "chats",
	state: {
		chats: [],
		selectedChatId: "",
		isGenerating: false,
		prompt: "",
	},
	actions: {
		create: (state, chat: Chat) => {
			state.chats.push(chat);
			state.selectedChatId = chat.id;
			state.isGenerating = true;

			const lastMessage =
				chat.messages.length === 0
					? null
					: chat.messages[chat.messages.length - 1];

			if (lastMessage && lastMessage.role === "user") {
				state.prompt = lastMessage.content;
			}

			return state;
		},

		reset: (state) => {
			state.selectedChatId = "";
			state.isGenerating = false;
			state.prompt = "";

			return state;
		},

    updatePrompt: (state, prompt: string) => {
      state.prompt = prompt;

      return state;
    },

		addMessage: (
			state,
			{ chatId, message }: { chatId: string; message: Message }
		) => {
			const chat = state.chats.find((chat) => chat.id === chatId);

			if (chat) {
				chat.messages.push(message);
			}

			if (message.role === "user") {
				state.isGenerating = true;
			}

			return state;
		},

		addResponse: (
			state,
			{
				chatId,
				messageId,
				content,
			}: { chatId: string; messageId: string; content: string }
		) => {
			const chat = state.chats.find((chat) => chat.id === chatId);

			if (chat) {
				const myMessage = chat.messages.find((m) => m.id === messageId);

				if (myMessage) {
					myMessage.updateContent(content);
				} else {
					const message = new Message({
						id: messageId,
						content,
						role: "model",
						createdAt: new Date(Date.now()),
					});

					chat.messages.push(message);
				}
			}

			return state;
		},

		stopGenerating: (state) => {
			state.isGenerating = false;

			return state;
		},

		selectChat: (state, chat: Chat) => {
			state.selectedChatId = chat.id;

			return state;
		},

		renameChat: (state, { chatId, name }: { chatId: string; name: string }) => {
			const chat = state.chats.find((chat) => chat.id === chatId);

			if (chat) {
				chat.name = name;
			}

			return state;
		},
	},

	operations: {
		getCurrentChat: (state, id: string) => {
			return state.chats.find((chat) => chat.id === id);
		},
	},
});
